import {
  Box,
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup,
  Stack,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Field, FieldProps, Form, FormikProps, useFormikContext } from "formik";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ContactField from "./FormComponents/ContactField";
import FormikSelectField from "./FormComponents/FormikSelectField";
import GeneralTextField from "./FormComponents/GeneralTextField";
import {
  AccountPayer,
  DocumentTypeEnum,
  FormValues,
  GasOptionEnum,
} from "./types";
import {
  formGasBothHomeUserValidationSchema,
  formGasBothWithCompanyValidationSchema,
} from "./FormComponents/Validations";
import { apiClient } from "src/api/apiService";
import Calculator from "./FormComponents/Calculator";
import FormContext from "src/context/FormContext";
import {
  hasTotalLoadDetails,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
  preSubmissionRequestUpdate,
  sendEicComms,
  showUpdatePremiseAlert,
  siteConditionsOptions,
  submitBlock,
  validatePlumberLicense,
} from "./helper";
import { useNavigate } from "react-router-dom";
import { getLodgeErrorTypeEnum } from "../../api/apiHelper";
import ResidentialGasLoadField from "./FormComponents/ResidentialGasLoadField";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import HomeUserLicencedPlumber from "./HomeUser/HomeUserLicencedPlumber";
import HomeUserSiteSupervisor from "./HomeUser/HomeUserSiteSupervisor";
import * as Yup from "yup";
import { useAppContext } from "src/context/AppContext";
import { EICStatusEnum, State } from "../Premises/types";
import { usePermissions } from "../../hooks/usePermissions";
import { LifeSupportSection } from "./FormComponents/LifeSupportSection";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { NewPremiseFormFileUploadFormikField } from "./NewPremise/NewPremiseFormFileUploadFormikField";

interface GasBothBlockProps {
  setFormLoadValues?: (values: FormValues) => void;
  homeUser: boolean;
  premiseId: string | null;
}

const GasBothBlock: React.FC<GasBothBlockProps> = ({
  setFormLoadValues,
  homeUser,
  premiseId,
}) => {
  const [showStreetNumber, setShowStreetNumber] = useState(false);
  const navigate = useNavigate();
  const {
    formState: { gasServices, isAccountReadOnly },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);
  const {
    context: { user },
  } = useAppContext();
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();
  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<FormValues>();
  const {
    propertyType,
    premiseAddress: { state },
    both: { siteConditions, meterLocation, pressure },
  } = values;
  const [open, setOpen] = useState(false);
  const displayContactFields =
    values.premiseAddress?.state === "NSW" ||
    values.premiseAddress?.state === "ACT" ||
    values.premiseAddress?.state === "QLD";
  useEffect(() => {
    if (state === "SA") {
      setFieldValue("both.buildingType", "existing");
    }
  }, [state]);

  useEffect(() => {
    if (!values.premiseAddress.streetNumber) {
      setShowStreetNumber(true);
    }
    //only run this once
  }, []);

  //cleaning up data from unused form pathways
  useEffect(() => {
    if (!siteConditions.includes("other")) {
      setFieldValue("both.otherSiteCondition", "");
    }
  }, [siteConditions]);

  useEffect(() => {
    if (pressure !== "other" && pressure !== "0") {
      setFieldValue("both.otherPressure", "");
    }
  }, [pressure]);

  useEffect(() => {
    if (meterLocation !== "other") {
      setFieldValue("both.otherMeterLocation", "");
    }
  }, [meterLocation]);

  const closeDialog = () => {
    setOpen(false);
  };
  const openDialog = () => {
    setOpen(true);
  };

  const saveGasBothBlock = async () => {
    const manipulatedValues =
      homeUser &&
      values.accountHolder === "person" &&
      !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
      user
        ? {
            ...values,
            accountPrimaryEmail: user.email,
            accountPrimaryFirstName: user.first_name,
            accountPrimaryLastName: user.last_name,
          }
        : values;
    try {
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "both",
        undefined,
        undefined,
        undefined,
        false,
        homeUser
      );
      setDisplayingSnackbar({
        message: "Gas block updated",
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save gas block",
        tone: "caution",
      });
    }
  };

  const isDigitalEic =
    homeUser ||
    (values.both.accountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const sendEicCommsOnLodge = async () => {
    if (isDigitalEic && values.both.eicStatus !== EICStatusEnum.EIC_ACCEPTED) {
      const { valid, message } = await sendEicComms(
        values.id,
        gasServices?.both?.requestId ?? null,
        "Notification re-sent.",
        "Account failed to save."
      );
      if (valid) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
            isDigitalEic,
            snackbarMessage: message,
          },
        });
      } else {
        setDisplayingSnackbar({
          message,
          tone: valid ? "positive" : "caution",
        });
      }
    }
  };

  const lodgeRequest = async () => {
    try {
      const manipulatedValues =
        isHomeUserFromPermissions &&
        values.accountHolder === "person" &&
        !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
        user
          ? {
              ...values,
              accountPrimaryEmail: user.email,
              accountPrimaryFirstName: user.first_name,
              accountPrimaryLastName: user.last_name,
            }
          : values;
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "both",
        {},
        undefined,
        undefined,
        isDigitalEic,
        homeUser,
        true
      );
      if (isDigitalEic) {
        await sendEicCommsOnLodge();
        return;
      }
      const { data } = await apiClient.put(
        `/submission/${gasServices?.both?.requestId}`
      );
      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
          journeyId: data.services[0].journey_id,
        },
      });
    } catch (error) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(error) ?? null);
    }
  };

  const siteConditionsError = errors?.both?.siteConditions;

  const appliancesChecklistHelpText = (
    <>
      <Text>Please download and complete </Text>
      <TextLink
        href={
          "https://www.originenergy.com.au/content/dam/origin/residential/docs/elec-gas/all-appliance-checklist-act-nsw.pdf"
        }
        target="_blank"
      >
        <Strong>Natural gas appliances checklist</Strong>
      </TextLink>
      <Text>PDF form.</Text>
    </>
  );
  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "gasBothBlock",
    Boolean(premiseId)
  );

  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xxlarge",
              md: "xxxlarge",
            }}
          >
            {/* Form container */}
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
            >
              {/* New or existing building */}
              {["NSW", "ACT"].includes(state) && (
                <FormFieldLayout>
                  <Field name="both.buildingType">
                    {({ field }: { field: FormikProps<FieldProps> }) => (
                      <RadioGroup
                        {...field}
                        id="new-existing"
                        name="both.buildingType"
                        label="New or existing building?"
                      >
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          flexDirection={{
                            xs: "column",
                            sm: "row",
                          }}
                        >
                          <Box marginRight="medium">
                            <Radio id="new" label="New" value="new" />
                          </Box>
                          <Radio
                            id="existing"
                            label="Existing"
                            value="existing"
                          />
                        </Box>
                      </RadioGroup>
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              {showStreetNumber && (
                <FormFieldLayout>
                  <Field
                    component={GeneralTextField}
                    name="premiseAddress.streetNumber"
                    label="Street No."
                    maxLength={200}
                  />
                </FormFieldLayout>
              )}

              <Stack
                space={{
                  xs: "xlarge",
                  md: "xxlarge",
                }}
              >
                {homeUser && displayContactFields ? (
                  <HomeUserLicencedPlumber type="both" />
                ) : (
                  <FormFieldLayout>
                    <ContactField
                      contactType="PLUMBER"
                      name="both.licensedPlumber"
                      label="Licensed plumber"
                      validatePlumberLicense={validatePlumberLicense}
                    />
                  </FormFieldLayout>
                )}

                {homeUser && displayContactFields ? (
                  <HomeUserSiteSupervisor type="both" />
                ) : (
                  <FormFieldLayout>
                    <ContactField
                      contactType="SUPERVISOR"
                      name="siteSupervisor"
                      label="Site supervisor"
                    />
                  </FormFieldLayout>
                )}

                {/* Pressure */}
                <FormFieldLayout multipleFields>
                  <Field
                    name="both.pressure"
                    label="Pressure"
                    component={FormikSelectField}
                    options={pressureOptions}
                  />
                  {/* Other pressure: "0" to represent "Other" */}
                  {values.both.pressure === "0" && (
                    <Field
                      component={GeneralTextField}
                      name="both.otherPressure"
                      label="Other pressure value (kPa)"
                      maxLength={200}
                    />
                  )}
                </FormFieldLayout>
                {/* End Pressure */}

                {/* Total Load Calculator */}
                {propertyType === "RESIDENTIAL" && (
                  <FormFieldLayout>
                    <Stack space="small">
                      <ResidentialGasLoadField
                        name="both.totalLoad"
                        label="Total load, MJ/h"
                        openDialog={openDialog}
                      />
                      {hasTotalLoadDetails(values.both.totalLoadDetails) && (
                        <Text variant="body-small" tone="neutralLight">
                          {populateTotalLoadSummary(
                            values.both.totalLoadDetails
                          )}
                        </Text>
                      )}
                    </Stack>
                  </FormFieldLayout>
                )}

                {/* Appliances Checklist */}
                {propertyType === "COMMERCIAL" && (
                  <Stack space="xxlarge">
                    <FormFieldLayout
                      multipleFields
                      text={appliancesChecklistHelpText}
                    >
                      <Text>Appliances checklist</Text>
                      <Field name="both.appliancesChecklistFiles">
                        {({ field }: FieldProps) => (
                          <NewPremiseFormFileUploadFormikField
                            draftId={gasServices?.both?.draftId ?? ""}
                            documentTypes={[
                              DocumentTypeEnum.APPLIANCE_CHECKLIST,
                            ]}
                            field={field}
                          />
                        )}
                      </Field>
                    </FormFieldLayout>
                    <FormFieldLayout multipleFields>
                      <Field
                        component={GeneralTextField}
                        name="both.totalLoad"
                        label="Total load, MJ/h"
                        maxLength={200}
                        type="number"
                      />
                    </FormFieldLayout>
                  </Stack>
                )}
                {/* End Appliances Checklist */}

                {/* Total load */}

                {["NSW", "ACT"].includes(state) && (
                  <FormFieldLayout multipleFields>
                    <CheckboxGroup
                      id="site-conditions"
                      label="Site conditions:"
                      error={Boolean(siteConditionsError)}
                      helperText={siteConditionsError?.toString()}
                    >
                      {siteConditionsOptions.map((option) => (
                        <Field
                          type="checkbox"
                          value={option.value}
                          name="both.siteConditions"
                          key={option.value}
                        >
                          {({ field }: { field: FormikProps<FieldProps> }) => (
                            <Checkbox
                              {...field}
                              key={option.value}
                              id={option.value}
                              label={option.label}
                            />
                          )}
                        </Field>
                      ))}
                    </CheckboxGroup>
                    {values.both.siteConditions.includes("other") && (
                      <Field
                        component={GeneralTextField}
                        name="both.otherSiteCondition"
                        label="Other site condition"
                        maxLength={30}
                      />
                    )}
                  </FormFieldLayout>
                )}
                {["NSW", "ACT"].includes(state) && (
                  <FormFieldLayout multipleFields>
                    <Field
                      name="both.meterLocation"
                      label="Meter location"
                      component={FormikSelectField}
                      options={meterLocationOptions}
                    />
                    {values.both.meterLocation === "other" && (
                      <Field
                        component={GeneralTextField}
                        name="both.otherMeterLocation"
                        label="Other meter location"
                        maxLength={200}
                      />
                    )}
                  </FormFieldLayout>
                )}

                {/* Site Plan */}
                <FormFieldLayout multipleFields>
                  <Text>Site plan</Text>
                  <Field name="both.sitePlanFiles">
                    {({ field }: FieldProps) => (
                      <NewPremiseFormFileUploadFormikField
                        draftId={gasServices?.both?.draftId ?? ""}
                        documentTypes={[DocumentTypeEnum.SITE_PLAN]}
                        field={field}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
                {/* End Site Plan */}
              </Stack>
            </Stack>
            {/* End Form container */}
            {open && (
              <Calculator
                appliancesFromFormik={values.both.totalLoadDetails}
                totalLoadFromFormik={values.both.totalLoad}
                closeDialog={closeDialog}
                setFieldValue={setFieldValue}
                gasOptionName={GasOptionEnum.BOTH}
              />
            )}

            <LifeSupportSection
              showPresentation={
                values.both?.accountPayer === AccountPayer.COMPANY &&
                !homeUser &&
                state === State.VIC
              }
              showWithFormFieldLayout={true}
              fieldProps={{
                name: "both.lifeSupport",
              }}
            />

            {/* Actions */}
            <PermissionsActionButtons
              primaryButtonProps={{
                onHandleClick: async () =>
                  await submitBlock({
                    values,
                    setErrors,
                    validationSchema: (homeUser
                      ? formGasBothHomeUserValidationSchema(
                          isAccountReadOnly,
                          isDigitalEic
                        )
                      : formGasBothWithCompanyValidationSchema(
                          isAccountReadOnly,
                          isDigitalEic
                        )) as Yup.ObjectSchema<any>,
                    context: {
                      state,
                      propertyType,
                      both: values.both,
                    },
                    gasOption: "both",
                    lodgeRequest,
                  }),
              }}
              secondaryButtonProps={{
                onHandleClick: saveGasBothBlock,
                disabled: gasServices?.both?.autoSubmit,
              }}
            />
            {/* End Actions */}
          </Stack>
        </Form>
      )}
    </>
  );
};

export default GasBothBlock;
